import { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'

const supportsAnchorAPI = CSS.supports('anchor-name', '--test')

export const usePopover = ({
  id,
  positionArea = 'bottom span-left',// set anchor position/alignment
} = {}) => {
  
  const idRef = useRef(id || `popover-${Math.random().toString(36).substr(2, 9)}`)
  const pid = idRef.current
  const popoverRef = useRef(null)
  const triggerRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  const anchorName = `--${pid}`

  // todo: remove fallback wiring once Safari/Firefox support Anchor API
  const updatePosition = useCallback(() => {
    if (!supportsAnchorAPI && triggerRef.current && popoverRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect()
      const popoverRect = popoverRef.current.getBoundingClientRect()
      
      let top = 0
      let left = 0
      
      // Position based on positionArea value for vertical alignment
      if (positionArea.includes('bottom')) {
        // Position below the trigger
        top = triggerRect.bottom + window.scrollY
      } else if (positionArea.includes('top')) {
        // Position above the trigger
        top = triggerRect.top - popoverRect.height + window.scrollY
      }
      
      // Position based on positionArea value for horizontal alignment
      if (positionArea.includes('span-left')) {
        // Align left edge of popover with left edge of trigger (extends to the right)
        left = triggerRect.right - popoverRect.width + window.scrollX
      } else if (positionArea.includes('span-right')) {
        // Align right edge of popover with right edge of trigger (extends to the left)
        left = triggerRect.left + window.scrollX
      } else {
        // Default center alignment
        left = triggerRect.left + (triggerRect.width / 2) - (popoverRect.width / 2) + window.scrollX
      }
      
      // Ensure the popover doesn't go off the left edge of the screen
      if (left < 0) left = 0;
      
      setPosition({ top, left })
    }
  }, [positionArea])

  // The props that need to be applied to the trigger element
  const triggerProps = useMemo(() => ({
    ref: triggerRef,
    'popovertarget': pid,
    'aria-details': pid,
    'aria-haspopup': 'dialog',
    style: supportsAnchorAPI ? {anchorName} : {},
  }), [])

  // matching props that need to be applied to the popover element
  const popoverProps = {
    id: pid,
    popoverRef,
    'popover': 'auto',
    className: `simple-popover ${anchorName}`,
    style: supportsAnchorAPI ? {
      positionAnchor: anchorName,
      positionArea,
    } : {},
    position,
    updatePosition,
    isOpen,
    setIsOpen,
  }

  const closePopover = useCallback(() => {
    if (popoverRef.current) {
      popoverRef.current.hidePopover()
    } else {
      setIsOpen(false)
    }
  }, [])
  
  return { triggerProps, popoverProps, closePopover }
}

export const Popover = props => {
  const {
    children,
    className,
    popoverRef,
    position,
    updatePosition,
    isOpen,
    setIsOpen,
    ...popoverProps
  } = props

  useEffect(() => {
    if (!popoverRef.current) {
      return
    }
    
    const handleToggle = ({ newState } = {}) => {
      if (popoverRef.current) {
        setIsOpen(newState === 'open')
        updatePosition()
      }
    }
    
    popoverRef.current.addEventListener('toggle', handleToggle)
    
    return () => {
      if (popoverRef.current) {
        popoverRef.current.removeEventListener('toggle', handleToggle)
        popoverRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!supportsAnchorAPI && isOpen) {
      let animationFrameId = null
      const handlePositionUpdate = () => {
        animationFrameId = requestAnimationFrame(updatePosition)
      }
      // Update position on resize or scroll
      window.addEventListener('resize', handlePositionUpdate, { passive: true})
      window.addEventListener('scroll', handlePositionUpdate)
      
      return () => {
        if (animationFrameId !== null) {
          cancelAnimationFrame(animationFrameId)
        }
        window.removeEventListener('resize', handlePositionUpdate, { passive: true})
        window.removeEventListener('scroll', handlePositionUpdate)
      }
    }
  }, [isOpen, updatePosition])

  if (!supportsAnchorAPI) {
    // Render using portal for fallback mode
    return createPortal(
      <div 
        {...popoverProps}
        className={`simple-popover fallback-popover ${className || ''}`} 
        ref={popoverRef}
        style={{
          ...popoverProps.style,
          position: 'fixed',
          top: `${position.top}px`,
          left: `${position.left}px`,
          zIndex: 1000,
        }}
      >
        {isOpen && children}
      </div>,
      document.body
    )
  }

  return (
    <div {...popoverProps} className={`simple-popover ${className || ''}`} ref={popoverRef}>
      {isOpen && children}
    </div>
  )
}